import { EdgeFade } from 'designSystem/components/EdgeFade'
import type { ReactNode } from 'react'
import styled, { css } from 'styled-components'

const LANDING_SPACING = '0.5rem'
const LANDING_COLUMN_GAP = '1.25rem'
const SCROLL_FADE_HEIGHT = '1.5rem'
const SCROLLBAR_WIDTH = '0.5rem'
const SCROLLBAR_GUTTER = '0.5rem'
const SCROLLBAR_LANE = `calc(${SCROLLBAR_GUTTER} + ${SCROLLBAR_WIDTH})`

const verticalScrollbarStyles = css`
  scrollbar-width: thin;

  &::-webkit-scrollbar {
    width: ${SCROLLBAR_WIDTH};
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 9999rem;
    background: var(--border);
  }
`

const Root = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
  height: 100%;
  padding: ${LANDING_SPACING} 2rem 0;
  gap: ${LANDING_SPACING};
  overflow: hidden;
  background-color: var(--card);
  --edge-fade-from: var(--card);
`

const Hero = styled.div`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 100%;
`

const ColumnWrapper = styled.div`
  display: flex;
  flex: 1;
  justify-content: center;
  width: 100%;
  min-height: 0;
`

const Column = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  width: 100%;
  max-width: 56.25rem;
  min-height: 0;
  overflow: visible;
`

const Body = styled.div`
  position: relative;
  display: flex;
  flex: 1;
  flex-direction: column;
  width: 100%;
  min-height: 0;
  gap: ${LANDING_COLUMN_GAP};
`

const SearchRegion = styled.div`
  position: relative;
  z-index: 2;
  flex-shrink: 0;
  width: 100%;
  background-color: var(--card);
`

const ScrollRow = styled.div`
  z-index: 1;
  display: flex;
  flex: 1;
  width: calc(100% + ${SCROLLBAR_LANE});
  min-height: 0;
  margin-top: calc(-1 * ${SCROLL_FADE_HEIGHT});
`

const ScrollTrack = styled.div`
  position: relative;
  flex: 1;
  min-width: 0;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior: contain;
  ${verticalScrollbarStyles}
`

const ScrollContent = styled.div`
  display: flex;
  flex-direction: column;
  width: calc(100% - ${SCROLLBAR_LANE});
  min-height: 100%;
  padding-top: ${SCROLL_FADE_HEIGHT};
  padding-bottom: 2rem;
  gap: ${LANDING_COLUMN_GAP};
`

export function LandingPageLayout({
  hero,
  search,
  children,
}: {
  hero: ReactNode
  search: ReactNode
  children: ReactNode
}) {
  return (
    <Root data-slot='landing-page-layout'>
      <Hero data-slot='landing-page-hero'>{hero}</Hero>
      <ColumnWrapper>
        <Column>
          <Body>
            <SearchRegion data-slot='landing-page-search'>
              {search}
              <EdgeFade placement='below' />
            </SearchRegion>
            <ScrollRow>
              <ScrollTrack data-slot='landing-page-scroll'>
                <ScrollContent>{children}</ScrollContent>
              </ScrollTrack>
            </ScrollRow>
            <EdgeFade placement='bottom' />
          </Body>
        </Column>
      </ColumnWrapper>
    </Root>
  )
}
